import React, { useState, useEffect } from "react";
import { Card, Row, Col, Form, InputGroup, Spinner, Button, Table } from "react-bootstrap";
import { ToastContainer, toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import DatePicker from "react-datepicker";
import "react-datepicker/dist/react-datepicker.css";
import axios from "axios";
import { var_api, image_api } from "../../../constant";
import defaultImg from './../../../images/task/img1.jpg';

const ExpenseReports = () => {
  const itemsPerPage = 15;

  const [reportData, setReportData] = useState([]);
  const [filteredData, setFilteredData] = useState([]);
  const [currentPage, setCurrentPage] = useState(1);
  const [searchQuery, setSearchQuery] = useState("");
  const [errors, setErrors] = useState({});
  const [loading, setLoading] = useState(true);
  const [grandTotal, setGrandTotal] = useState(0);
  const [expenseOptions, setExpenseOptions] = useState([]);

  // 🔹 Default dates
  const today = new Date();
  const firstDayOfMonth = new Date(today.getFullYear(), today.getMonth(), 1);
  const lastDayOfMonth = new Date(today.getFullYear(), today.getMonth() + 1, 0);

  const [fromDate, setFromDate] = useState(firstDayOfMonth);
  const [toDate, setToDate] = useState(lastDayOfMonth);

  // 🔹 Fetch Expense Names
  const fetchExpenseNameOptions = async () => {
    const warehouse_id = localStorage.getItem("warehouse_id") || 1;
    try {
      const res = await axios.get(`${var_api}sa_veemart_expensename_master/getbywarehouse-active/${warehouse_id}`);
      if (res.data && Array.isArray(res.data)) {
        setExpenseOptions(res.data);
      }
    } catch (error) {
      console.error("Failed to load expense name list");
    }
  };

  // 🔹 Fetch Expense Report Data
  const handleGenerateList = async (from, to) => {
    if (!from || !to) {
      setErrors({ dateRange: "Please select both From and To dates" });
      toast.warning("Please select both From and To dates");
      return;
    }

    if (from > to) {
      setErrors({ dateRange: "From date cannot be after To date" });
      toast.warning("From date cannot be after To date");
      return;
    }

    setLoading(true);
    try {
      const warehouse_id = localStorage.getItem("warehouse_id") || 1;
      
      // Format dates to DD-MM-YYYY for API
      const formatDate = (date) => {
        const day = String(date.getDate()).padStart(2, '0');
        const month = String(date.getMonth() + 1).padStart(2, '0');
        const year = date.getFullYear();
        return `${day}-${month}-${year}`;
      };

      const payload = {
        warehouse_id: parseInt(warehouse_id),
        from_date: formatDate(from),
        to_date: formatDate(to)
      };

      // POST request with body
      const res = await axios.post(`${var_api}veemart_expenses/getByDateRange`, payload);

      if (res.data && res.data.data && Array.isArray(res.data.data)) {
        const data = res.data.data;
        setReportData(data);
        
        // Filter if search query exists
        let filtered = data;
        if (searchQuery.trim()) {
          filtered = data.filter((item) =>
            Object.values(item).some(val => 
              String(val).toLowerCase().includes(searchQuery.trim().toLowerCase())
            )
          );
        }
        
        setFilteredData(filtered);
        
        // Calculate grand total
        const total = data.reduce((sum, item) => sum + parseFloat(item.amount || 0), 0);
        setGrandTotal(total);
        
        setCurrentPage(1);
        setErrors({});
      } else {
        setReportData([]);
        setFilteredData([]);
        setGrandTotal(0);
      }
    } catch (error) {
      toast.error("Error fetching expense report");
      console.error("Error:", error);
      setReportData([]);
      setFilteredData([]);
      setGrandTotal(0);
    } finally {
      setLoading(false);
    }
  };

  // 🔹 Auto-fetch on first load
  useEffect(() => {
    fetchExpenseNameOptions();
    handleGenerateList(fromDate, toDate);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // 🔹 Auto-refresh when date range changes
  useEffect(() => {
    if (fromDate && toDate) {
      handleGenerateList(fromDate, toDate);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [fromDate, toDate]);

  // 🔹 Auto-filter when search text changes
  useEffect(() => {
    if (searchQuery.trim()) {
      const filtered = reportData.filter((item) =>
        Object.values(item).some(val => 
          String(val).toLowerCase().includes(searchQuery.trim().toLowerCase())
        )
      );
      setFilteredData(filtered);
      
      // Recalculate total for filtered data
      const total = filtered.reduce((sum, item) => sum + parseFloat(item.amount || 0), 0);
      setGrandTotal(total);
    } else {
      setFilteredData(reportData);
      
      // Recalculate total for all data
      const total = reportData.reduce((sum, item) => sum + parseFloat(item.amount || 0), 0);
      setGrandTotal(total);
    }
  }, [searchQuery, reportData]);

  const totalPages = Math.ceil(filteredData.length / itemsPerPage);
  const paginatedData = filteredData.slice(
    (currentPage - 1) * itemsPerPage,
    currentPage * itemsPerPage
  );

  // 🔹 Get expense name by ID
  const getExpenseNameById = (id) => {
    const match = expenseOptions.find((exp) => exp.id === id);
    return match ? match.expense_name : "-";
  };

  // 🔹 Format date for display
  const formatDisplayDate = (dateString) => {
    if (!dateString) return "-";
    return dateString; // Already in DD-MM-YYYY format
  };

  // 🔹 Format date range for title
  const formatDateRange = (from, to) => {
    const format = (date) => date.toLocaleDateString('en-GB', {
      day: '2-digit',
      month: 'short',
      year: 'numeric'
    });
    return `${format(from)} to ${format(to)}`;
  };

  return (
    <>
      {/* 🔹 Full Page Loader */}
      {loading && (
        <div
          style={{
            position: "fixed",
            top: 0,
            left: 0,
            width: "100%",
            height: "100%",
            backgroundColor: "rgba(255, 255, 255, 0.9)",
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
            zIndex: 2000,
          }}
        >
          <div className="text-center">
            <Spinner animation="border" variant="primary" style={{ width: "3rem", height: "3rem" }} />
            <div className="mt-3 fw-bold">Loading Expense Reports...</div>
          </div>
        </div>
      )}

      <Card className="shadow-sm">
        <Card.Body>
          <h4 className="mb-4">
            <i className="bi bi-file-earmark-text me-2"></i> Expense Report
            <small className="text-muted ms-2 fs-6">
              {formatDateRange(fromDate, toDate)}
            </small>
          </h4>

          <Row className="align-items-end mb-4">
            <Col md={8}>
              <Form.Label className="fw-bold mb-2">
                Select Date Range
              </Form.Label>
              <div className="d-flex align-items-center gap-3">
                <div className="d-flex align-items-center gap-2">
                  <Form.Label className="mb-0">From:</Form.Label>
                  <DatePicker
                    className="form-control"
                    selected={fromDate}
                    onChange={(date) => setFromDate(date)}
                    selectsStart
                    startDate={fromDate}
                    endDate={toDate}
                    dateFormat="dd/MM/yyyy"
                    isClearable
                    placeholderText="From Date"
                  />
                </div>
                
                <div className="d-flex align-items-center gap-2">
                  <Form.Label className="mb-0">To:</Form.Label>
                  <DatePicker
                    className="form-control"
                    selected={toDate}
                    onChange={(date) => setToDate(date)}
                    selectsEnd
                    startDate={fromDate}
                    endDate={toDate}
                    minDate={fromDate}
                    dateFormat="dd/MM/yyyy"
                    isClearable
                    placeholderText="To Date"
                  />
                </div>
                
                <Button 
                  variant="outline-primary" 
                  size="sm"
                  onClick={() => handleGenerateList(fromDate, toDate)}
                >
                  <i className="bi bi-arrow-clockwise me-1"></i> Refresh
                </Button>
              </div>
            </Col>

            <Col md={4}>
              <Form.Control
                type="text"
                placeholder="Search expenses..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="mb-2"
              />
              <div className="text-muted small">
                Search by expense name, description, amount, or date
              </div>
            </Col>
          </Row>

          {errors.dateRange && (
            <div className="alert alert-warning mb-3">{errors.dateRange}</div>
          )}

          {/* Summary Cards */}
          <Row className="mb-4">
            <Col md={4}>
              <Card className="border-primary">
                <Card.Body className="py-3">
                  <div className="d-flex justify-content-between align-items-center">
                    <div>
                      <h6 className="text-muted mb-1">Total Expenses</h6>
                      <h4 className="mb-0">{filteredData.length}</h4>
                    </div>
                    <div className="text-primary fs-1">
                      <i className="bi bi-receipt"></i>
                    </div>
                  </div>
                </Card.Body>
              </Card>
            </Col>
            <Col md={4}>
              <Card className="border-success">
                <Card.Body className="py-3">
                  <div className="d-flex justify-content-between align-items-center">
                    <div>
                      <h6 className="text-muted mb-1">Total Amount</h6>
                      <h4 className="mb-0 text-success">
                        ₹ {grandTotal.toLocaleString("en-IN")}
                      </h4>
                    </div>
                    <div className="text-success fs-1">
                      <i className="bi bi-currency-rupee"></i>
                    </div>
                  </div>
                </Card.Body>
              </Card>
            </Col>
            <Col md={4}>
              <Card className="border-info">
                <Card.Body className="py-3">
                  <div className="d-flex justify-content-between align-items-center">
                    <div>
                      <h6 className="text-muted mb-1">Average per Expense</h6>
                      <h4 className="mb-0 text-info">
                        ₹ {filteredData.length > 0 ? (grandTotal / filteredData.length).toFixed(2).toLocaleString("en-IN") : "0.00"}
                      </h4>
                    </div>
                    <div className="text-info fs-1">
                      <i className="bi bi-graph-up"></i>
                    </div>
                  </div>
                </Card.Body>
              </Card>
            </Col>
          </Row>

          {/* Table */}
          <div className="table-responsive">
            <Table >
              <thead >
                <tr>
                  <th>#</th>
                  <th>Proof</th>
                  <th>Expense Name</th>
                  <th>Description</th>
                  <th>Amount (₹)</th>
                  <th>Date</th>
                  <th>Status</th>
                </tr>
              </thead>
              <tbody>
                {paginatedData.length === 0 ? (
                  <tr>
                    <td colSpan="7" className="text-center text-muted py-4">
                      {loading ? "Loading..." : "No expenses found for the selected date range"}
                    </td>
                  </tr>
                ) : (
                  paginatedData.map((item, idx) => (
                    <tr key={item.id || idx}>
                      <td>{(currentPage - 1) * itemsPerPage + idx + 1}</td>
                      <td>
                        <img
                          src={item.proof_document ? `${image_api}${item.proof_document.replace(/^test\//, '')}` : defaultImg}
                          alt="proof"
                          width="40"
                          height="40"
                          className="rounded"
                          onClick={() => {
                            if (item.proof_document) {
                              window.open(`${image_api}${item.proof_document.replace(/^test\//, '')}`, '_blank');
                            }
                          }}
                          style={{ 
                            cursor: item.proof_document ? 'pointer' : 'default',
                            opacity: item.proof_document ? 1 : 0.5
                          }}
                        />
                      </td>
                      <td>
                        {item.expense_name || getExpenseNameById(item.expense_id)}
                      </td>
                      <td>{item.description || "-"}</td>
                      <td className="fw-bold">
                        ₹ {parseFloat(item.amount || 0).toFixed(2).toLocaleString("en-IN")}
                      </td>
                      <td>{formatDisplayDate(item.expense_date)}</td>
                      <td>
                        <span className={`badge ${item.active_status === 1 ? 'bg-success' : 'bg-danger'}`}>
                          {item.active_status === 1 ? 'Active' : 'Inactive'}
                        </span>
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </Table>
          </div>

          {/* Pagination */}
          {totalPages > 1 && (
            <div className="d-flex justify-content-between align-items-center mt-4">
              <div className="text-muted">
                Showing {paginatedData.length} of {filteredData.length} expenses
              </div>
              <div className="d-flex">
                <Button
                  variant="outline-secondary"
                  size="sm"
                  onClick={() => setCurrentPage(prev => Math.max(prev - 1, 1))}
                  disabled={currentPage === 1}
                  className="me-2"
                >
                  Previous
                </Button>
                
                {[...Array(totalPages)].map((_, idx) => (
                  <Button
                    key={idx}
                    variant={currentPage === idx + 1 ? "primary" : "outline-secondary"}
                    size="sm"
                    onClick={() => setCurrentPage(idx + 1)}
                    className="me-2"
                  >
                    {idx + 1}
                  </Button>
                ))}
                
                <Button
                  variant="outline-secondary"
                  size="sm"
                  onClick={() => setCurrentPage(prev => Math.min(prev + 1, totalPages))}
                  disabled={currentPage === totalPages}
                >
                  Next
                </Button>
              </div>
            </div>
          )}

        
        </Card.Body>
      </Card>

      <ToastContainer />
    </>
  );
};

export default ExpenseReports;